/**
 * FSS Landing Page Tracking
 * Universal event tracking for Meta Pixel, GA4/Google Ads (gtag), TikTok Pixel,
 * Pinterest, Snapchat, and GTM dataLayer. Auto-attaches to FSS landing pages
 * via existing CSS class names — no section file changes required.
 *
 * Fires:
 *   - LandingPage_View (on init)
 *   - Attribution_Captured (when UTMs are present in URL)
 *   - Scroll_Depth (10/25/50/75/90/100% milestones)
 *   - Time_OnPage (30s/60s/120s/300s heartbeats)
 *   - Section_Visible (when landing page sub-sections enter viewport)
 *   - CTA_Primary_Click / CTA_Secondary_Click / CTA_TikTok_Click
 *   - Hero_CTA_Click / Final_CTA_Click (position-aware)
 *   - Category_Click, Featured_Product_Click
 *   - Sale_Product_Click, Bundle_Click, Hot_Deal_Click
 *   - Filter_Click, FAQ_Open, FAQ_Close
 *   - Creator_Video_Click, Video_Play_Click
 *   - Form_Field_Focus, Form_Submit
 *   - Outbound_Click, Page_Exit
 *
 * Persistence:
 *   - UTM parameters captured into sessionStorage AND 30-day cookie
 *   - UTMs injected into Shopify cart attributes (carry through to order)
 *
 * Debug:
 *   - Append ?fss_debug=1 to URL to see every event in console
 *   - Or set window.FSS_TRACK_DEBUG = true before this script loads
 */
(function() {
  'use strict';

  // ============== CONFIG ==============
  var DEBUG = (function() {
    try {
      return location.search.indexOf('fss_debug=1') > -1 || window.FSS_TRACK_DEBUG === true;
    } catch (e) { return false; }
  })();

  var CFG = window.FSS_TRACK_CONFIG || {};
  var PAGE_NAME = CFG.pageName || (document.body && document.body.dataset.fssPage) || 'unknown';
  var PAGE_TITLE = CFG.pageTitle || document.title || '';
  var TEMPLATE = CFG.template || '';

  var firedKeys = {};
  var initialized = false;

  // ============== UNIVERSAL DISPATCH ==============
  function track(eventName, params) {
    params = Object.assign({
      page: PAGE_NAME,
      page_title: PAGE_TITLE,
      template: TEMPLATE
    }, params || {});

    if (DEBUG) {
      console.log('%c[FSS Track] ' + eventName, 'color:#1365b0;font-weight:bold;', params);
    }

    // Meta Pixel — fbq('trackCustom', name, params)
    try {
      if (typeof fbq === 'function') fbq('trackCustom', eventName, params);
    } catch (e) {}

    // Google (GA4 + Google Ads via gtag)
    try {
      if (typeof gtag === 'function') gtag('event', eventName, params);
    } catch (e) {}

    // TikTok Pixel
    try {
      if (typeof ttq === 'object' && ttq && typeof ttq.track === 'function') {
        ttq.track(eventName, params);
      }
    } catch (e) {}

    // Pinterest Tag
    try {
      if (typeof pintrk === 'function') pintrk('track', 'custom', Object.assign({ event_name: eventName }, params));
    } catch (e) {}

    // Snapchat Pixel
    try {
      if (typeof snaptr === 'function') snaptr('track', 'CUSTOM_EVENT_1', Object.assign({ event_name: eventName }, params));
    } catch (e) {}

    // GTM dataLayer — always push, GTM can map this to any tag
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: eventName }, params));
  }

  // ============== UTM ATTRIBUTION ==============
  var UTM_KEYS = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
    'gclid', 'fbclid', 'ttclid', 'msclkid', 'wbraid', 'gbraid'
  ];

  function readCookie(name) {
    try {
      var match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.$?*|{}()[\]\\\/+^]/g, '\\$&') + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : null;
    } catch (e) { return null; }
  }

  function writeCookie(name, value, days) {
    try {
      var expires = new Date(Date.now() + (days || 30) * 86400000).toUTCString();
      document.cookie = name + '=' + encodeURIComponent(value) + '; expires=' + expires + '; path=/; SameSite=Lax';
    } catch (e) {}
  }

  function captureUTM() {
    var current = {};
    try {
      var params = new URLSearchParams(window.location.search);
      UTM_KEYS.forEach(function(k) {
        if (params.has(k)) current[k] = params.get(k);
      });
    } catch (e) {}

    // Read prior attribution (first-touch persisted, last-touch persisted)
    var firstTouch = {};
    var lastTouch = {};
    try {
      var ft = readCookie('fss_utm_first');
      if (ft) firstTouch = JSON.parse(ft);
      var lt = sessionStorage.getItem('fss_utm') || readCookie('fss_utm_last');
      if (lt) lastTouch = JSON.parse(lt);
    } catch (e) {}

    // If current URL has UTMs, treat as new touch
    if (Object.keys(current).length > 0) {
      // Save first-touch only if none exists
      if (Object.keys(firstTouch).length === 0) {
        firstTouch = current;
        writeCookie('fss_utm_first', JSON.stringify(firstTouch), 365);
      }
      // Update last-touch
      lastTouch = current;
      try { sessionStorage.setItem('fss_utm', JSON.stringify(lastTouch)); } catch (e) {}
      writeCookie('fss_utm_last', JSON.stringify(lastTouch), 30);

      // Inject into Shopify cart attributes (carries through to order)
      try {
        var cartAttrs = {};
        Object.keys(lastTouch).forEach(function(k) { cartAttrs[k] = lastTouch[k]; });
        Object.keys(firstTouch).forEach(function(k) { cartAttrs[k + '_first'] = firstTouch[k]; });
        cartAttrs.fss_landing_page = PAGE_NAME;
        cartAttrs.fss_landing_url = window.location.href.split('?')[0];

        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: cartAttrs })
        }).catch(function() {});
      } catch (e) {}

      track('Attribution_Captured', current);
    }

    // Fire view event with attribution context
    var viewParams = {
      first_touch_source: firstTouch.utm_source || '',
      first_touch_campaign: firstTouch.utm_campaign || '',
      last_touch_source: lastTouch.utm_source || '',
      last_touch_campaign: lastTouch.utm_campaign || '',
      has_gclid: !!(current.gclid || lastTouch.gclid),
      has_fbclid: !!(current.fbclid || lastTouch.fbclid),
      has_ttclid: !!(current.ttclid || lastTouch.ttclid)
    };
    track('LandingPage_View', viewParams);
  }

  // ============== SCROLL DEPTH ==============
  function initScrollDepth() {
    var marks = [10, 25, 50, 75, 90, 100];
    var fired = {};

    function check() {
      var doc = document.documentElement;
      var scrollTop = window.scrollY || doc.scrollTop || 0;
      var height = Math.max(doc.scrollHeight - window.innerHeight, 1);
      var pct = Math.round((scrollTop / height) * 100);

      for (var i = 0; i < marks.length; i++) {
        var m = marks[i];
        if (pct >= m && !fired[m]) {
          fired[m] = true;
          track('Scroll_Depth', { depth: m, depth_label: m + '%' });
        }
      }
    }

    var throttle = null;
    window.addEventListener('scroll', function() {
      if (throttle) return;
      throttle = setTimeout(function() { check(); throttle = null; }, 250);
    }, { passive: true });
  }

  // ============== TIME ON PAGE ==============
  function initTimeOnPage() {
    var marks = [
      { secs: 30, label: '30s' },
      { secs: 60, label: '1m' },
      { secs: 120, label: '2m' },
      { secs: 300, label: '5m' }
    ];

    marks.forEach(function(m) {
      setTimeout(function() {
        if (!document.hidden) {
          track('Time_OnPage', { seconds: m.secs, time_label: m.label });
        }
      }, m.secs * 1000);
    });
  }

  // ============== SECTION VISIBILITY ==============
  // Auto-detects FSS sub-section areas and fires when each comes into view
  function initSectionVisibility() {
    if (!('IntersectionObserver' in window)) return;

    var SECTION_SELECTORS = {
      '.fss-hero-split': 'Hero',
      '.fss-hero-dark': 'Hero',
      '.fss-hero-tiktok': 'Hero',
      '.fss-hero-video': 'Hero',
      '.fss-hero-sale': 'Hero',
      '.fss-hero-bundle': 'Hero',
      '.fss-trust-strip': 'TrustStrip',
      '.fss-cat-grid': 'Categories',
      '.fss-fp-grid': 'FeaturedProducts',
      '.fss-local-inner': 'LocalStore',
      '.fss-reviews-grid': 'Reviews',
      '.fss-faq-list': 'FAQ',
      '.fss-final-cta': 'FinalCTA',
      '.fss-stats': 'Stats',
      '.fss-benefits-grid': 'Benefits',
      '.fss-ba-grid': 'BeforeAfter',
      '.fss-table': 'ComparisonTable',
      '.fss-sale-grid': 'SaleProducts',
      '.fss-hot-grid': 'HotDeals',
      '.fss-bundle-grid': 'Bundles',
      '.fss-creator-feed': 'CreatorFeed',
      '.fss-tiktok-deal': 'FlashDeal'
    };

    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          var name = entry.target.getAttribute('data-fss-section-name');
          var key = 'visible_' + name;
          if (!firedKeys[key]) {
            firedKeys[key] = true;
            track('Section_Visible', { section: name });
            observer.unobserve(entry.target);
          }
        }
      });
    }, { threshold: 0.4 });

    Object.keys(SECTION_SELECTORS).forEach(function(sel) {
      document.querySelectorAll(sel).forEach(function(el) {
        el.setAttribute('data-fss-section-name', SECTION_SELECTORS[sel]);
        observer.observe(el);
      });
    });
  }

  // ============== CLICK TRACKING (AUTO-ATTACHED) ==============
  // Maps FSS class names to event names + extracts useful params
  function initClickTracking() {
    // Order matters — more specific selectors should come first
    var CLICK_MAP = [
      // CTAs — detect position (hero / final / body)
      { sel: '.fss-final-cta a.fss-btn-orange', event: 'Final_CTA_Primary_Click' },
      { sel: '.fss-final-cta a.fss-btn-outline-white', event: 'Final_CTA_Secondary_Click' },
      { sel: '.fss-hero-split a.fss-btn-orange, .fss-hero-dark a.fss-btn-orange, .fss-hero-video a.fss-btn, .fss-hero-bundle a.fss-btn-orange', event: 'Hero_CTA_Primary_Click' },
      { sel: '.fss-hero-split a.fss-btn-outline, .fss-hero-dark a.fss-btn-outline-white, .fss-hero-bundle a.fss-btn-outline-white', event: 'Hero_CTA_Secondary_Click' },
      { sel: '.fss-hero-tiktok a.fss-btn-tiktok', event: 'Hero_TikTok_CTA_Click' },

      // Generic button taps if not in hero/final
      { sel: 'a.fss-btn-orange', event: 'CTA_Primary_Click' },
      { sel: 'a.fss-btn-outline, a.fss-btn-outline-white', event: 'CTA_Secondary_Click' },
      { sel: 'a.fss-btn-tiktok', event: 'CTA_TikTok_Click' },
      { sel: 'a.fss-btn', event: 'CTA_Click' },

      // Content blocks
      { sel: '.fss-cat-card', event: 'Category_Click' },
      { sel: '.fss-fp-card', event: 'Featured_Product_Click' },
      { sel: '.fss-sale-card a, .fss-sale-cta', event: 'Sale_Product_Click' },
      { sel: '.fss-hot-card a, .btn-hot', event: 'Hot_Deal_CTA_Click' },
      { sel: '.fss-bundle-btn', event: 'Bundle_Add_Click' },
      { sel: '.fss-bundle-card', event: 'Bundle_View_Click' },
      { sel: '.fss-filter-btn', event: 'Filter_Click' },
      { sel: '.fss-creator-card', event: 'Creator_Video_Click' },
      { sel: '.fss-yt-play, .fss-video-wrap', event: 'Video_Play_Click' },
      { sel: '.fss-local-row a, .fss-local-inner a.fss-btn', event: 'Local_Store_Click' }
    ];

    document.addEventListener('click', function(e) {
      for (var i = 0; i < CLICK_MAP.length; i++) {
        var rule = CLICK_MAP[i];
        var el = e.target.closest(rule.sel);
        if (el) {
          var params = collectParams(el);
          track(rule.event, params);
          return; // only fire one event per click
        }
      }

      // Generic outbound link tracking (fallback)
      var link = e.target.closest('a[href]');
      if (link && link.href) {
        try {
          var url = new URL(link.href, location.href);
          if (url.host !== location.host && url.protocol.indexOf('http') === 0) {
            track('Outbound_Click', { href: link.href, host: url.host });
          }
        } catch (err) {}
      }
    });
  }

  function collectParams(el) {
    var params = {};
    var label = (el.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 60);
    if (label) params.label = label;
    if (el.href) params.href = el.href;

    // Extract product info if available
    var productImg = el.querySelector('img[alt]');
    if (productImg && productImg.alt) params.product = productImg.alt.slice(0, 60);

    // Extract brand/name from FSS product card structure
    var brand = el.querySelector('.fss-fp-brand, .fss-sale-brand');
    var name = el.querySelector('.fss-fp-name, .fss-sale-name, .fss-bundle-name');
    if (brand && brand.textContent) params.brand = brand.textContent.trim();
    if (name && name.textContent) params.product_name = name.textContent.trim().slice(0, 60);

    // Capture any data-fss-* attributes for custom params
    if (el.attributes) {
      for (var i = 0; i < el.attributes.length; i++) {
        var attr = el.attributes[i];
        if (attr.name.indexOf('data-fss-') === 0 && attr.name !== 'data-fss-event' && attr.name !== 'data-fss-section-name') {
          params[attr.name.replace('data-fss-', '')] = attr.value;
        }
      }
    }

    return params;
  }

  // ============== FAQ OPEN/CLOSE ==============
  function initFAQTracking() {
    document.addEventListener('click', function(e) {
      var q = e.target.closest('.fss-faq-q');
      if (!q) return;
      var item = q.closest('.fss-faq-item');
      if (!item) return;

      // Setting wins async after the click handler; check 50ms later
      setTimeout(function() {
        var isOpen = item.classList.contains('open');
        var question = (q.textContent || '').trim().slice(0, 60);
        track(isOpen ? 'FAQ_Open' : 'FAQ_Close', { question: question });
      }, 50);
    });
  }

  // ============== FORM TRACKING ==============
  function initFormTracking() {
    document.addEventListener('focus', function(e) {
      var el = e.target;
      if (!el.matches || !el.matches('input, textarea, select')) return;
      var fieldName = el.name || el.id || el.type || 'unknown';
      var key = 'form_focus_' + fieldName;
      if (firedKeys[key]) return;
      firedKeys[key] = true;
      track('Form_Field_Focus', { field: fieldName });
    }, true);

    document.addEventListener('submit', function(e) {
      var form = e.target;
      track('Form_Submit', {
        form_id: form.id || 'unknown',
        action: form.action || '',
        method: form.method || ''
      });
    });

    // Track newsletter signups specifically
    document.addEventListener('submit', function(e) {
      var form = e.target;
      if (form && (form.action || '').indexOf('contact') > -1) {
        track('Newsletter_Signup', { source: 'footer' });
      }
    });
  }

  // ============== PAGE EXIT ==============
  function initExitTracking() {
    var sent = false;
    function fireExit() {
      if (sent) return;
      sent = true;
      var timeOnPage = Math.round((Date.now() - startTime) / 1000);
      track('Page_Exit', { time_on_page_secs: timeOnPage });
    }
    var startTime = Date.now();
    document.addEventListener('visibilitychange', function() {
      if (document.hidden) fireExit();
    });
    window.addEventListener('pagehide', fireExit);
  }

  // ============== PUBLIC API ==============
  window.FSS_TRACK = {
    send: track,
    setPage: function(name) { PAGE_NAME = name; },
    setDebug: function(b) { DEBUG = b; },
    getAttribution: function() {
      try {
        return {
          first: JSON.parse(readCookie('fss_utm_first') || '{}'),
          last: JSON.parse(readCookie('fss_utm_last') || sessionStorage.getItem('fss_utm') || '{}')
        };
      } catch (e) { return { first: {}, last: {} }; }
    }
  };

  // ============== INIT ==============
  function init() {
    if (initialized) return;
    initialized = true;

    if (DEBUG) {
      console.log('%c[FSS Track] Initialized', 'color:#1365b0;font-weight:bold;', {
        page: PAGE_NAME,
        title: PAGE_TITLE,
        template: TEMPLATE,
        pixels: {
          meta: typeof fbq === 'function',
          google: typeof gtag === 'function',
          tiktok: typeof ttq === 'object',
          pinterest: typeof pintrk === 'function',
          snapchat: typeof snaptr === 'function',
          gtm: !!(window.google_tag_manager || window.dataLayer)
        }
      });
    }

    captureUTM();
    initScrollDepth();
    initTimeOnPage();
    initSectionVisibility();
    initClickTracking();
    initFAQTracking();
    initFormTracking();
    initExitTracking();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
